import { supabase, DbClient } from '../lib/supabase';
import { ClientData, PersonalValue, Audience } from '../types';
import { AuthService } from './authService';

export type DataKey =
  | 'demographics'
  | 'personal_values'
  | 'psychological_drivers'
  | 'hobbies_habits'
  | 'daily_routine'
  | 'brand_affinity'
  | 'custom_charts'
  | 'flexible_sections'
  | 'insights'
  | 'ui_settings'
  | 'page_content';

type DatasetConfig = {
  table: string;
  column: string;
  defaultValue: any;
};

const PERSONAL_VALUE_CATEGORY_MAP: Record<string, PersonalValue['category']> = {
  nature: 'openness',
  creativity: 'openness',
  stimulation: 'openness',
  independence: 'openness',
  pleasure: 'openness',
  influence: 'self-enhancement',
  achievement: 'self-enhancement',
  authority: 'self-enhancement',
  reputation: 'self-enhancement',
  equality: 'self-transcendence',
  tolerance: 'self-transcendence',
  caring: 'self-transcendence',
  dependability: 'conservation',
  conformity: 'conservation',
  safety: 'conservation',
  security: 'conservation',
  duty: 'conservation',
  tradition: 'conservation',
  humility: 'conservation',
};

const normalizeValueCategory = (
  name: string | undefined,
  fallback: PersonalValue['category'] = 'openness'
): PersonalValue['category'] => {
  if (!name) return fallback;
  const key = name.toLowerCase().replace(/[^a-z]/g, '');
  return PERSONAL_VALUE_CATEGORY_MAP[key] ?? fallback;
};

function getAuthToken(): string {
  const session = AuthService.getSession();
  if (!session || !session.token) {
    throw new Error('No active session');
  }
  return session.token;
}

async function invokeAdminWrite(payload: {
  operation: string;
  table: string;
  data?: any;
  id?: string;
  clientId?: string;
  audienceId?: string;
  onConflict?: string;
}): Promise<any> {
  const token = getAuthToken();
  const { data, error } = await supabase.functions.invoke('admin-client-write', {
    headers: { 'x-admin-session': token },
    body: payload,
  });
  if (error) throw error;
  return data;
}

export const CLIENT_DATASET_MAP: Record<DataKey, DatasetConfig> = {
  demographics: {
    table: 'client_demographics',
    column: 'data',
    defaultValue: null,
  },
  personal_values: {
    table: 'client_personal_values',
    column: 'values',
    defaultValue: [],
  },
  psychological_drivers: {
    table: 'client_psychological_drivers',
    column: 'drivers',
    defaultValue: [],
  },
  hobbies_habits: {
    table: 'client_hobbies_habits',
    column: 'hobbies',
    defaultValue: [],
  },
  daily_routine: {
    table: 'client_daily_routine',
    column: 'routines',
    defaultValue: [],
  },
  brand_affinity: {
    table: 'client_brand_affinity',
    column: 'affinity',
    defaultValue: null,
  },
  custom_charts: {
    table: 'client_custom_charts',
    column: 'charts',
    defaultValue: [],
  },
  flexible_sections: {
    table: 'client_flexible_sections',
    column: 'sections',
    defaultValue: [],
  },
  insights: {
    table: 'client_insights',
    column: 'insights',
    defaultValue: [],
  },
  ui_settings: {
    table: 'client_ui_settings',
    column: 'settings',
    defaultValue: null,
  },
  page_content: {
    table: 'client_page_content',
    column: 'content',
    defaultValue: null,
  },
};

const extractPersonalValuesFromSections = (
  sections: any,
  existing?: PersonalValue[]
): PersonalValue[] => {
  if (!Array.isArray(sections)) return existing ?? [];

  const seen = new Map<string, PersonalValue>();

  if (Array.isArray(existing)) {
    existing.forEach((item) => {
      if (!item || typeof item.name !== 'string') return;
      const key = item.name.trim().toLowerCase().replace(/\s+/g, ' ');
      if (!key) return;
      seen.set(key, { ...item });
    });
  }

  const coalesceString = (...candidates: any[]): string | undefined => {
    for (const candidate of candidates) {
      if (typeof candidate === 'string') {
        const trimmed = candidate.trim();
        if (trimmed) return trimmed;
      }
    }
    return undefined;
  };

  const pushValue = (
    name: string,
    value: number,
    details?: (Partial<PersonalValue> & Record<string, any>) | null
  ) => {
    if (!name || typeof value !== 'number' || Number.isNaN(value)) return;
    const key = name.trim();
    const normalizedKey = key.toLowerCase().replace(/\s+/g, ' ');
    const fallbackCategory =
      existing?.find((v) => v.name.toLowerCase() === normalizedKey)?.category;
    const existingEntry = seen.get(normalizedKey);
    const category = existingEntry?.category ?? normalizeValueCategory(key, fallbackCategory);
    const next: PersonalValue = {
      name: key,
      // Latest parsed value should win; avoid carrying older higher values across uploads.
      value,
      category,
      longTitle: existingEntry?.longTitle ?? coalesceString(details?.longTitle, details?.description),
      description: existingEntry?.description ?? details?.description ?? null,
      tip: existingEntry?.tip ?? details?.tip ?? null,
    };

    // Prefer richer descriptor if new data provides and existing lacks
    if (!next.longTitle) {
      const descriptor = coalesceString(
        (details as any)?.descriptor,
        (details as any)?.subtitle,
        (details as any)?.detail
      );
      if (descriptor) next.longTitle = descriptor;
    }

    seen.set(normalizedKey, next);
  };

  sections.forEach((section: any) => {
    if (!section || typeof section !== 'object') return;
    const title = String(section.title || '').toLowerCase();
    if (!title.includes('personal value')) return;

    const data = section.data ?? section.values ?? section.items ?? [];

    const visit = (node: any) => {
      if (!node) return;
      if (Array.isArray(node)) {
        node.forEach(visit);
        return;
      }
      if (typeof node !== 'object') return;

      const name = node.name || node.label || node.title;
      const valueCandidate =
        node.value ?? node.index ?? node.score ?? node.amount ?? node.percentage;
      const value =
        typeof valueCandidate === 'number'
          ? valueCandidate
          : typeof valueCandidate === 'string'
            ? Number(valueCandidate.replace(/[^0-9.\-]/g, ''))
            : Number.NaN;

      if (!Number.isNaN(value)) {
        const descriptor = coalesceString(
          (node as any).descriptor,
          (node as any).descriptorTitle,
          (node as any).descriptive_title,
          (node as any).subheading,
          (node as any).subHeading
        );
        const longTitle = coalesceString(
          (node as any).longTitle,
          (node as any).long_title,
          descriptor,
          (node as any).subtitle,
          (node as any).subTitle,
          (node as any).heading,
          (node as any).titleSecondary
        );
        const description = coalesceString(
          (node as any).description,
          (node as any).summary,
          (node as any).narrative,
          (node as any).detail,
          (node as any).body,
          (node as any).text
        );
        const tip = coalesceString(
          (node as any).tip,
          (node as any).recommendation,
          (node as any).guidance,
          (node as any).takeaway,
          (node as any)['tip_text']
        );
        pushValue(String(name || '').trim(), value, { longTitle, description, tip, descriptor });
      }

      Object.values(node).forEach((child) => visit(child));
    };

    visit(data);
  });

  return seen.size > 0 ? Array.from(seen.values()) : existing ?? [];
};

export class ClientService {
  static async getAllClients(): Promise<DbClient[]> {
    const { data, error } = await supabase
      .from('clients')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  }

  // ==================== AUDIENCE METHODS ====================

  static async getClientAudiences(clientId: string): Promise<Audience[]> {
    const { data, error } = await supabase
      .from('client_audiences')
      .select('*')
      .eq('client_id', clientId)
      .order('display_order', { ascending: true });

    if (error) throw error;

    return (data || []).map((row: any) => ({
      id: row.id,
      clientId: row.client_id,
      name: row.name,
      slug: row.slug,
      displayOrder: row.display_order,
      isDefault: row.is_default,
      createdAt: row.created_at,
      updatedAt: row.updated_at,
    }));
  }

  static async createAudience(clientId: string, name: string): Promise<Audience> {
    // Check max 3 audiences
    const existing = await ClientService.getClientAudiences(clientId);
    if (existing.length >= 3) {
      throw new Error('Maximum of 3 audiences per client');
    }

    // Generate slug from name
    const slug = name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

    // Get next display order
    const nextOrder = existing.length > 0
      ? Math.max(...existing.map(a => a.displayOrder)) + 1
      : 0;

    const result = await invokeAdminWrite({
      operation: 'insert',
      table: 'client_audiences',
      data: {
        client_id: clientId,
        name,
        slug,
        display_order: nextOrder,
        is_default: existing.length === 0, // First audience is default
      },
    });

    const data = result.data[0];
    return {
      id: data.id,
      clientId: data.client_id,
      name: data.name,
      slug: data.slug,
      displayOrder: data.display_order,
      isDefault: data.is_default,
      createdAt: data.created_at,
      updatedAt: data.updated_at,
    };
  }

  static async updateAudience(audienceId: string, updates: { name?: string }): Promise<Audience> {
    const updatePayload: any = {};

    if (updates.name) {
      updatePayload.name = updates.name;
      updatePayload.slug = updates.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
    }

    const result = await invokeAdminWrite({
      operation: 'update',
      table: 'client_audiences',
      id: audienceId,
      data: updatePayload,
    });

    const data = result.data[0];
    return {
      id: data.id,
      clientId: data.client_id,
      name: data.name,
      slug: data.slug,
      displayOrder: data.display_order,
      isDefault: data.is_default,
      createdAt: data.created_at,
      updatedAt: data.updated_at,
    };
  }

  static async deleteAudience(audienceId: string): Promise<void> {
    await invokeAdminWrite({
      operation: 'delete',
      table: 'client_audiences',
      id: audienceId,
    });
  }

  // ==================== END AUDIENCE METHODS ====================

  static async getClientBySlug(slug: string, audienceId?: string): Promise<ClientData | null> {
    // Get client basic info
    const { data: client, error: clientError } = await supabase
      .from('clients')
      .select('*')
      .eq('slug', slug)
      .single();

    if (clientError || !client) return null;

    // Fetch audiences for this client
    const audiences = await ClientService.getClientAudiences(client.id);

    // If no audienceId provided, use the default audience (or first one)
    let effectiveAudienceId = audienceId;
    if (!effectiveAudienceId && audiences.length > 0) {
      const defaultAudience = audiences.find(a => a.isDefault) || audiences[0];
      effectiveAudienceId = defaultAudience.id;
    }

    const fetchLatestData = async (key: DataKey) => {
      const config = CLIENT_DATASET_MAP[key];

      // Build query - filter by audience_id if we have one
      let query = supabase
        .from(config.table)
        .select(`${config.column}`)
        .eq('client_id', client.id);

      // Only filter by audience_id for tables that support it
      const audienceEnabledTables = [
        'client_demographics',
        'client_personal_values',
        'client_psychological_drivers',
        'client_hobbies_habits',
        'client_daily_routine',
        'client_brand_affinity',
        'client_flexible_sections',
      ];

      if (effectiveAudienceId && audienceEnabledTables.includes(config.table)) {
        query = query.eq('audience_id', effectiveAudienceId);
      }

      const { data: row, error, status } = await query.maybeSingle();

      if (error && status !== 406) {
        throw error;
      }

      if (!row || (row as any)[config.column] === undefined || (row as any)[config.column] === null) {
        return config.defaultValue;
      }

      let val = (row as any)[config.column];

      // Handle potential string-encoded JSON (including double-encoding)
      // Keep parsing while the value is a string that looks like JSON
      let parseAttempts = 0;
      while (typeof val === 'string' && parseAttempts < 3) {
        const trimmed = val.trim();
        if (!trimmed.startsWith('[') && !trimmed.startsWith('{')) break;
        try {
          val = JSON.parse(trimmed);
          parseAttempts++;
        } catch (e) {
          console.warn(`Failed to parse JSON for ${key} (attempt ${parseAttempts + 1}):`, e);
          break;
        }
      }

      if (key === 'daily_routine') {
        console.log('[ClientService] fetchLatestData daily_routine:', val);
      }

      return val;
    };

    const fetchDemographicsData = async () => {
      const jsonData = await fetchLatestData('demographics');
      if (jsonData && typeof jsonData === 'object') {
        const merged = ClientService.mergeDemographicsObjects(
          ClientService.getDefaultDemographics(),
          jsonData as Partial<ReturnType<typeof ClientService.getDefaultDemographics>>
        );
        if (merged) return merged;
      }

      const [genderRes, ageRes, eduRes, empRes] = await Promise.all([
        supabase.from('client_gender_data').select('*').eq('client_id', client.id).single(),
        supabase.from('client_age_group_data').select('*').eq('client_id', client.id).single(),
        supabase.from('client_education_data').select('*').eq('client_id', client.id).single(),
        supabase.from('client_employment_data').select('*').eq('client_id', client.id).single(),
      ]);

      const ageData = ageRes.data as any;
      const eduData = eduRes.data as any;
      const empData = empRes.data as any;
      const genderData = genderRes.data as any;

      return {
        targetableIds: '0',
        gender: genderData ? { male: genderData.male, female: genderData.female } : { male: 0, female: 0 },
        ageGroups: ageData ? {
          '18-24': ageData['18-24'], '25-34': ageData['25-34'], '35-44': ageData['35-44'],
          '45-49': ageData['45-49'], '50-54': ageData['50-54'], '55-64': ageData['55-64'],
          '65+': ageData['65+'],
        } : {
          '18-24': 0, '25-34': 0, '35-44': 0, '45-49': 0, '50-54': 0, '55-64': 0, '65+': 0,
        },
        education: eduData ? {
          lessThanHighSchool: eduData.less_than_high_school_degree,
          highSchool: eduData.high_school_degree,
          someCollege: eduData.some_college,
          collegeDegree: eduData.college_degree,
          postGrad: eduData.post_grad_degree,
        } : {
          lessThanHighSchool: 0, highSchool: 0, someCollege: 0, collegeDegree: 0, postGrad: 0,
        },
        employment: empData ? {
          fullTime: empData.full_time,
          selfEmployed: empData.self_employed,
          partTime: empData.part_time,
          unemployed: empData.unemployed,
          retired: 0,
          index: 100,
        } : {
          fullTime: 0, selfEmployed: 0, partTime: 0, unemployed: 0, retired: 0, index: 100,
        },
        income: {
          '<$25k': 0, '$25-50k': 0, '$50-75k': 0, '$75-100k': 0, '$100-150k': 0, '$150-200k': 0, '$200-250k': 0, '$250k+': 0,
        },
        maritalStatus: {
          single: 0, married: 0, divorced: 0, widowed: 0,
        },
        children: {
          none: 0, one: 0, twoOrThree: 0, fourOrFive: 0, sixPlus: 0,
        },
      };
    };

    const [
      demographics,
      personalValuesRaw,
      psychologicalDrivers,
      hobbiesAndHabits,
      dailyRoutine,
      brandAffinity,
      customCharts,
      flexibleSections,
      insights,
      uiSettings,
      pageContent,
    ] = await Promise.all([
      fetchDemographicsData(),
      fetchLatestData('personal_values'),
      fetchLatestData('psychological_drivers'),
      fetchLatestData('hobbies_habits'),
      fetchLatestData('daily_routine'),
      fetchLatestData('brand_affinity'),
      fetchLatestData('custom_charts'),
      fetchLatestData('flexible_sections'),
      fetchLatestData('insights'),
      fetchLatestData('ui_settings'),
      fetchLatestData('page_content'),
    ]);

    let personalValues = Array.isArray(personalValuesRaw) ? personalValuesRaw : [];

    if ((!personalValues || personalValues.length === 0) && Array.isArray(flexibleSections)) {
      const derived = extractPersonalValuesFromSections(flexibleSections, personalValues);
      if (derived.length > 0) {
        personalValues = derived;
        await ClientService.updateClientData(client.id, 'personal_values', derived);
      }
    }

    return {
      id: client.id,
      name: client.name,
      slug: client.slug,
      logo: client.logo_url,
      headline: client.headline,
      introduction: client.introduction,
      pagePasswordHash: client.page_password_hash || null,
      demographics: demographics || null,
      personalValues: personalValues || [],
      psychologicalDrivers: psychologicalDrivers || [],
      hobbiesAndHabits: hobbiesAndHabits || [],
      dailyRoutine: dailyRoutine || [],
      brandAffinity: brandAffinity || null,
      customCharts: customCharts,
      flexibleSections: flexibleSections,
      insights: insights,
      uiSettings: uiSettings,
      pageContent: pageContent,
      audiences: audiences,
      currentAudienceId: effectiveAudienceId || undefined,
      createdAt: client.created_at,
      updatedAt: client.updated_at
    };
  }

  static async createClient(clientData: Partial<ClientData>): Promise<DbClient> {
    const result = await invokeAdminWrite({
      operation: 'insert',
      table: 'clients',
      data: {
        name: clientData.name!,
        slug: clientData.slug!,
        logo_url: clientData.logo,
        headline: clientData.headline!,
        introduction: clientData.introduction!,
        page_password_hash: clientData.pagePasswordHash || null,
      },
    });

    return result.data[0];
  }

  static async updateClient(id: string, updates: Partial<ClientData>): Promise<DbClient> {
    const result = await invokeAdminWrite({
      operation: 'update',
      table: 'clients',
      id,
      data: {
        name: updates.name,
        slug: updates.slug,
        logo_url: updates.logo,
        headline: updates.headline,
        introduction: updates.introduction,
        page_password_hash: updates.pagePasswordHash,
      },
    });

    return result.data[0];
  }

  static async updateClientData(clientId: string, dataType: string, data: any, audienceId?: string): Promise<void> {
    const key = dataType as DataKey;

    // Tables that support audience_id filtering
    const audienceEnabledTables = [
      'client_demographics',
      'client_personal_values',
      'client_psychological_drivers',
      'client_hobbies_habits',
      'client_daily_routine',
      'client_brand_affinity',
      'client_flexible_sections',
    ];

    if (key === 'demographics') {
      const { gender, ageGroups, education, employment } = data || {};

      const normalized = {
        targetableIds: data?.targetableIds,
        gender: gender || {},
        ageGroups: ageGroups || {},
        education: education || {},
        employment: employment || {},
      };

      // Persist canonical JSON copy for reliable reads
      const demographicsPayload = {
        targetableIds: normalized.targetableIds,
        gender: normalized.gender,
        ageGroups: normalized.ageGroups,
        education: normalized.education,
        employment: normalized.employment,
      };

      const jsonPayload: Record<string, any> = {
        client_id: clientId,
        data: demographicsPayload,
      };

      if (audienceId) {
        jsonPayload.audience_id = audienceId;
      }

      // Use edge function for upsert
      await invokeAdminWrite({
        operation: 'upsert',
        table: 'client_demographics',
        data: jsonPayload,
        onConflict: audienceId ? 'client_id,audience_id' : 'client_id',
      });

      // Best-effort sync to legacy tables for charts or other consumers
      const syncTasks: Array<Promise<any>> = [];

      if (gender) {
        syncTasks.push(
          ClientService.replaceLegacyRow('client_gender_data', clientId, () => ({
            male: gender.male ?? 0,
            female: gender.female ?? 0,
          }))
        );
      }

      if (ageGroups) {
        const ageVariants = ['18-24', '25-34', '35-44', '45-49', '50-54', '55-64', '65+'] as const;
        syncTasks.push(
          ClientService.replaceLegacyRow('client_age_group_data', clientId, () => {
            const canonical: Record<string, number> = {};
            ageVariants.forEach((key) => {
              canonical[key] = ageGroups[key] ?? 0;
            });
            return canonical;
          })
        );
      }

      if (education) {
        syncTasks.push(
          ClientService.replaceLegacyRow('client_education_data', clientId, () => ({
            less_than_high_school_degree: education.lessThanHighSchool ?? 0,
            high_school_degree: education.highSchool ?? 0,
            some_college: education.someCollege ?? 0,
            college_degree: education.collegeDegree ?? 0,
            post_grad_degree: education.postGrad ?? 0,
          }))
        );
      }

      if (employment) {
        syncTasks.push(
          ClientService.replaceLegacyRow('client_employment_data', clientId, () => ({
            full_time: employment.fullTime ?? 0,
            self_employed: employment.selfEmployed ?? 0,
            part_time: employment.partTime ?? 0,
            unemployed: employment.unemployed ?? 0,
          }))
        );
      }

      await Promise.allSettled(syncTasks);
      return;
    }

    const config = CLIENT_DATASET_MAP[key];

    if (!config) {
      console.warn('Unknown data type passed to updateClientData:', dataType);
      return;
    }

    // Ensure data is not a string-encoded JSON (parse it if so)
    let normalizedData = data;
    if (typeof normalizedData === 'string') {
      const trimmed = normalizedData.trim();
      if (trimmed.startsWith('[') || trimmed.startsWith('{')) {
        try {
          normalizedData = JSON.parse(trimmed);
        } catch (e) {
          console.warn(`updateClientData: Could not parse string data for ${dataType}, saving as-is`);
        }
      }
    }

    const payload: Record<string, any> = {
      client_id: clientId,
    };
    payload[config.column] = normalizedData;

    if (audienceId && audienceEnabledTables.includes(config.table)) {
      payload.audience_id = audienceId;
    }

    // Use edge function for upsert
    await invokeAdminWrite({
      operation: 'upsert',
      table: config.table,
      data: payload,
      onConflict: (audienceId && audienceEnabledTables.includes(config.table)) ? 'client_id,audience_id' : 'client_id',
    });
  }

  static async deleteClient(id: string): Promise<void> {
    await invokeAdminWrite({
      operation: 'delete',
      table: 'clients',
      id,
    });
  }

  private static async replaceLegacyRow(
    table: string,
    clientId: string,
    builder: () => Record<string, any>
  ) {
    try {
      await invokeAdminWrite({
        operation: 'delete_by_client',
        table,
        clientId,
      });
    } catch (error) {
      console.warn(`Failed to clear legacy ${table} row`, error);
      return;
    }

    try {
      const record = { client_id: clientId, ...builder() };
      await invokeAdminWrite({
        operation: 'insert',
        table,
        data: record,
      });
    } catch (error) {
      console.warn(`Failed to insert legacy ${table} row`, error);
    }
  }

  private static mergeDemographicsObjects(
    base: ReturnType<typeof ClientService.getDefaultDemographics>,
    patch?: Partial<ReturnType<typeof ClientService.getDefaultDemographics>> | null
  ) {
    if (!patch || typeof patch !== 'object') return base;
    const next = JSON.parse(JSON.stringify(base));

    const toNumeric = (value: any): number | null => {
      if (value === null || value === undefined) return null;
      if (typeof value === 'number' && Number.isFinite(value)) return value;
      if (typeof value === 'string') {
        const cleaned = value.replace(/[^0-9.\-]/g, '');
        if (!cleaned) return null;
        const parsed = Number(cleaned);
        return Number.isFinite(parsed) ? parsed : null;
      }
      if (typeof value === 'object') {
        const candidates = ['value', 'composition', 'percent', 'percentage', 'score', 'index', 'amount'];
        for (const key of candidates) {
          if (key in value) {
            const nested = toNumeric((value as any)[key]);
            if (nested !== null) return nested;
          }
        }
      }
      return null;
    };

    if (patch.targetableIds !== undefined && patch.targetableIds !== null) {
      next.targetableIds = String(patch.targetableIds);
    }

    const mergeGroup = (target: Record<string, number>, source?: Record<string, any>) => {
      if (!source) return;
      Object.entries(source).forEach(([key, value]) => {
        if (!(key in target)) return;
        const numeric = toNumeric(value);
        if (numeric !== null) {
          target[key] = numeric;
        }
      });
    };

    mergeGroup(next.gender, patch.gender as any);
    mergeGroup(next.ageGroups, patch.ageGroups as any);
    mergeGroup(next.education, patch.education as any);
    mergeGroup(next.income, patch.income as any);
    mergeGroup(next.maritalStatus, patch.maritalStatus as any);
    mergeGroup(next.children, patch.children as any);

    if (patch.employment && typeof patch.employment === 'object') {
      const employment = patch.employment as Record<string, any>;

      const EMPLOYMENT_KEY_MAP: Record<string, keyof typeof next.employment> = {
        full_time: 'fullTime',
        self_employed: 'selfEmployed',
        part_time: 'partTime',
        unemployed: 'unemployed',
        homemaker: 'homemaker',
        student: 'student',
        retired: 'retired',
        full_time_index: 'fullTimeIndex',
        self_employed_index: 'selfEmployedIndex',
        composition_primary: 'compositionPrimary',
        composition: 'compositionPrimary',
      };

      const resolveEmploymentKey = (key: string): keyof typeof next.employment | undefined => {
        if (key in next.employment) return key as keyof typeof next.employment;
        const normalized = key.replace(/[^a-z0-9]+/gi, '_').toLowerCase();
        return EMPLOYMENT_KEY_MAP[normalized];
      };

      Object.entries(employment).forEach(([rawKey, value]) => {
        const key = resolveEmploymentKey(String(rawKey));
        if (!key) return;
        const numeric = toNumeric(value);
        if (numeric !== null) {
          (next.employment as any)[key] = numeric;
        }

        if (value && typeof value === 'object') {
          const indexValue = toNumeric((value as any).index ?? (value as any).score);
          const hasPrimaryKey = key === 'fullTime' || key === 'selfEmployed' || key === 'partTime';
          if (indexValue !== null && hasPrimaryKey) {
            const indexKey = `${key}Index`;
            if (indexKey in next.employment) {
              (next.employment as any)[indexKey] = indexValue;
            } else if (key === 'fullTime') {
              (next.employment as any).index = indexValue;
            }
          }
        }
      });
    }

    return next;
  }

  private static getDefaultDemographics() {
    return {
      targetableIds: '0',
      gender: { male: 50, female: 50 },
      ageGroups: {
        '18-24': 0, '25-34': 0, '35-44': 0, '45-49': 0,
        '50-54': 0, '55-64': 0, '65+': 0
      },
      income: {
        '<$25k': 0, '$25-50k': 0, '$50-75k': 0, '$75-100k': 0,
        '$100-150k': 0, '$150-200k': 0, '$200-250k': 0, '$250k+': 0
      },
      employment: {
        fullTime: 0,
        fullTimeIndex: 0,
        selfEmployed: 0,
        selfEmployedIndex: 0,
        partTime: 0,
        unemployed: 0,
        homemaker: 0,
        student: 0,
        compositionPrimary: 0,
        retired: 0,
        index: 100
      },
      education: {
        lessThanHighSchool: 0, highSchool: 0, someCollege: 0,
        collegeDegree: 0, postGrad: 0
      },
      maritalStatus: { single: 0, married: 0, divorced: 0, widowed: 0 },
      children: { none: 0, one: 0, twoOrThree: 0, fourOrFive: 0, sixPlus: 0 }
    };
  }

  private static getDefaultBrandAffinity() {
    return {
      retailers: [],
      restaurants: [],
      hotels: [],
      airlines: [],
      autoBrands: []
    };
  }

  static async getClientData(clientId: string): Promise<Record<string, any>> {
    const entries = await Promise.all(
      Object.entries(CLIENT_DATASET_MAP).map(async ([key, config]) => {
        const { data, error, status } = await supabase
          .from(config.table)
          .select(`${config.column}`)
          .eq('client_id', clientId)
          .maybeSingle();

        if (error && status !== 406) {
          throw error;
        }

        return [key, data ? (data as any)[config.column] : config.defaultValue] as const;
      })
    );

    return Object.fromEntries(entries);
  }
}

export default ClientService;
